/**
 * Tamil explanatory composition writing studio API client.
 */
import { createHttpClient } from '../../../api/http';
import { resolveStudentTutorHttpBase } from '../../../api/tutorHttpBases';

function tutorRestApiPrefix(): string {
  const explicit = import.meta.env.VITE_TUTOR_REST_API_PREFIX?.trim();
  if (explicit === '/tutor' || explicit === '/v1/tutor') return explicit;
  return '/v1/tutor';
}

const PREFIX = `${tutorRestApiPrefix()}/tamil-composition-writing`;
const client = createHttpClient(resolveStudentTutorHttpBase());

export const TAMIL_COMPOSITION_ACTIVITY_ID = 'tamil_sec3_g3_u5_explanatory_composition';

export type TamilCompositionErrorType =
  | 'எழுத்துப்பிழை'
  | 'சொற்பிழை'
  | 'இலக்கணப்பிழை'
  | 'பொருட்பிழை';

export interface TamilBodyParagraph {
  id: string;
  focus: string;
  text: string;
  order: number;
}

export interface TamilCompositionDraft {
  draft_id: string;
  activity_id: string;
  topic_title_ta: string;
  introduction_text: string;
  body_paragraphs: TamilBodyParagraph[];
  conclusion_text: string;
  brainstorm_state: Record<string, unknown>;
  outline_state: Record<string, unknown>;
  current_coaching_step: string;
  word_count: number;
  min_submit_word_count: number;
  status: string;
  version: number;
  latest_submission_version: number;
  last_autosaved_at?: string;
  target_writing_section?: string | null;
  section_statuses?: Record<string, string>;
  active_review_snapshot_id?: string | null;
}

export interface TamilCompositionSectionProgress {
  section_key: string;
  label_ta: string;
  status: string;
}

export interface TamilCompositionHomeStatus {
  ok: boolean;
  activity_id: string;
  topic_title_ta: string;
  composition_status: string;
  current_coaching_step: string;
  current_section?: string | null;
  target_writing_section?: string | null;
  section_progress: TamilCompositionSectionProgress[];
  next_action: string;
  next_section_label_ta?: string | null;
  latest_score?: {
    content_score: number;
    language_score: number;
    total_score: number;
  } | null;
  draft_id?: string | null;
  updated_at?: string;
}

export interface TamilCompositionReviewSnapshot {
  snapshot_id: string;
  draft_id: string;
  section_key: string;
  section_type: string;
  snapshot_version: number;
  source_mode: string;
  text: string;
  draft_version_at_snapshot: number;
  created_at?: string;
}

export interface TamilCompositionErrorDiagnostic {
  error_type: TamilCompositionErrorType;
  paragraph_id: string;
  text_span: string;
  explanation_ta: string;
  suggestion_ta: string;
  severity: 'low' | 'medium' | 'high';
}

export interface TamilCompositionReview {
  content_score: number;
  language_score: number;
  total_score: number;
  content_band?: 'excellent' | 'good' | 'satisfactory' | 'limited' | 'weak';
  language_band?: 'excellent' | 'good' | 'satisfactory' | 'limited' | 'weak';
  overall_feedback_ta: string;
  content_feedback_ta?: string;
  language_feedback_ta?: string;
  strengths_ta: string[];
  improvements_ta: string[];
  error_diagnostics: TamilCompositionErrorDiagnostic[];
  revision_priorities_ta: string[];
  rubric_id?: string;
  rubric_version?: string | number;
  feedback?: {
    content?: string;
    language?: string;
    overall?: string;
  };
}

export interface TamilCompositionSubmission {
  submission_id: string;
  draft_id: string;
  version_number: number;
  submitted_at?: string;
  word_count?: number;
  review?: TamilCompositionReview | null;
  score?: {
    content_score: number;
    language_score: number;
    total_score: number;
    content_band?: string;
    language_band?: string;
    content_max: number;
    language_max: number;
    total_max: number;
    rubric_version?: string;
  } | null;
}

export interface TamilCompositionBootstrap {
  activity_id: string;
  topic_title_ta: string;
  min_submit_word_count: number;
  draft: TamilCompositionDraft | null;
  submissions: TamilCompositionSubmission[];
  rubric: {
    content_max: number;
    language_max: number;
    total_max: number;
    rubric_id?: string;
    rubric_version?: string | number;
  };
}

export type TamilCompositionFlushResult =
  | { ok: true; version: number }
  | { ok: false; error?: string };

export async function fetchTamilCompositionBootstrap(
  activityId: string = TAMIL_COMPOSITION_ACTIVITY_ID,
): Promise<TamilCompositionBootstrap> {
  const r = await client.get<TamilCompositionBootstrap>(`${PREFIX}/activities/${activityId}/bootstrap`);
  return r.data;
}

export async function autosaveTamilCompositionDraft(
  activityId: string,
  body: {
    expected_version?: number;
    introduction_text?: string;
    body_paragraphs?: TamilBodyParagraph[];
    conclusion_text?: string;
    brainstorm_state?: Record<string, unknown>;
    outline_state?: Record<string, unknown>;
    current_coaching_step?: string;
    idempotency_key?: string;
  },
): Promise<{ ok: boolean; draft: TamilCompositionDraft }> {
  const r = await client.patch<{ ok: boolean; draft: TamilCompositionDraft }>(
    `${PREFIX}/activities/${activityId}/draft`,
    body,
  );
  return r.data;
}

export async function submitTamilCompositionDraft(
  draftId: string,
  body: { expected_version: number; idempotency_key?: string },
): Promise<{ ok: boolean; submission: TamilCompositionSubmission }> {
  const r = await client.post<{ ok: boolean; submission: TamilCompositionSubmission }>(
    `${PREFIX}/drafts/${draftId}/submit`,
    body,
  );
  return r.data;
}

export async function getTamilCompositionLiveWritingContext(
  draftId: string,
): Promise<{
  ok: boolean;
  live_writing_context?: Record<string, unknown> | null;
  draft_revision?: number | null;
  word_count?: number;
}> {
  const r = await client.get(`${PREFIX}/drafts/${draftId}/live-writing-context`);
  return r.data;
}

export async function getTamilCompositionActivityLiveWritingContext(
  activityId: string = TAMIL_COMPOSITION_ACTIVITY_ID,
): Promise<{
  ok: boolean;
  live_writing_context?: Record<string, unknown> | null;
  draft_revision?: number | null;
  word_count?: number;
  current_coaching_step?: string;
  tool_state?: Record<string, unknown> | null;
}> {
  const r = await client.get(`${PREFIX}/activities/${activityId}/live-writing-context`);
  return r.data;
}

export async function reviseTamilCompositionFromSubmission(
  draftId: string,
  body: { expected_version: number; submission_id: string },
): Promise<{ ok: boolean; draft: TamilCompositionDraft }> {
  const r = await client.post<{ ok: boolean; draft: TamilCompositionDraft }>(
    `${PREFIX}/drafts/${draftId}/revise-from-submission`,
    body,
  );
  return r.data;
}

export async function fetchTamilCompositionHomeStatus(
  activityId: string = TAMIL_COMPOSITION_ACTIVITY_ID,
): Promise<TamilCompositionHomeStatus> {
  const r = await client.get<TamilCompositionHomeStatus>(
    `${PREFIX}/activities/${activityId}/home-status`,
  );
  return r.data;
}

export async function enterTamilCompositionWaitingForDraft(
  draftId: string,
  body: {
    expected_version: number;
    section_key: string;
    brainstorm_state?: Record<string, unknown>;
    outline_state?: Record<string, unknown>;
  },
): Promise<{
  ok: boolean;
  draft: TamilCompositionDraft;
  navigate_to_writing_workspace?: boolean;
  pause_live_session?: boolean;
  target_section?: string;
}> {
  const r = await client.post(`${PREFIX}/drafts/${draftId}/enter-waiting`, body);
  return r.data;
}

export async function checkTamilCompositionWithTutor(
  draftId: string,
  body: {
    expected_version: number;
    section_key: string;
    source_mode?: string;
    confirmed_text?: string;
    upload_id?: string;
  },
): Promise<{
  ok: boolean;
  draft: TamilCompositionDraft;
  snapshot: TamilCompositionReviewSnapshot;
  resume_live_session?: boolean;
  review_mode?: boolean;
}> {
  const r = await client.post(`${PREFIX}/drafts/${draftId}/check-with-tutor`, body);
  return r.data;
}

export async function getTamilCompositionTutorResumeContext(
  draftId: string,
): Promise<Record<string, unknown>> {
  const r = await client.get(`${PREFIX}/drafts/${draftId}/tutor-resume-context`);
  return r.data;
}

export async function markTamilCompositionSectionReviewed(
  draftId: string,
  body: { expected_version: number; section_key: string },
): Promise<{ ok: boolean; draft: TamilCompositionDraft; show_continue?: boolean }> {
  const r = await client.post(`${PREFIX}/drafts/${draftId}/mark-section-reviewed`, body);
  return r.data;
}

export async function markTamilCompositionSectionNeedsRevision(
  draftId: string,
  body: { expected_version: number; section_key: string },
): Promise<{
  ok: boolean;
  draft: TamilCompositionDraft;
  navigate_to_writing_workspace?: boolean;
  pause_live_session?: boolean;
}> {
  const r = await client.post(`${PREFIX}/drafts/${draftId}/mark-section-needs-revision`, body);
  return r.data;
}

export async function continueTamilCompositionAfterReviewed(
  draftId: string,
  body: { expected_version: number },
): Promise<{
  ok: boolean;
  draft: TamilCompositionDraft;
  resume_live_session?: boolean;
  next_section?: string | null;
  next_section_label_ta?: string;
}> {
  const r = await client.post(`${PREFIX}/drafts/${draftId}/continue-after-reviewed`, body);
  return r.data;
}

export async function beginTamilCompositionSectionReview(
  draftId: string,
  body: { expected_version: number; section_key: string },
): Promise<{ ok: boolean; draft: TamilCompositionDraft }> {
  const r = await client.post(`${PREFIX}/drafts/${draftId}/begin-section-review`, body);
  return r.data;
}
